import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Copy, Check, ExternalLink, KeyRound, RefreshCw } from 'lucide-react'
import {
  getAuthInfo,
  startDeviceFlow,
  pollDeviceFlow,
  type DeviceFlowStartResponse,
} from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { AppShell } from '@/components/layout/app-shell'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'

const TOKEN_KEY = 'aip_dns_access_token'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function decodeJwt(token: string | null): Record<string, unknown> | null {
  if (!token) return null
  try {
    const part = token.split('.')[1]
    if (!part) return null
    const padded = part.replace(/-/g, '+').replace(/_/g, '/')
    const b64 = padded + '='.repeat((4 - (padded.length % 4)) % 4)
    return JSON.parse(atob(b64))
  } catch {
    return null
  }
}

function fmtUnix(ts: unknown): string {
  if (typeof ts !== 'number') return '—'
  return new Date(ts * 1000).toLocaleString()
}

function expiresInLabel(exp: unknown): string {
  if (typeof exp !== 'number') return ''
  const secs = exp - Math.floor(Date.now() / 1000)
  if (secs <= 0) return '(expired)'
  if (secs < 60) return `(${secs}s)`
  if (secs < 3600) return `(${Math.round(secs / 60)} min)`
  return `(${Math.round(secs / 3600)} h)`
}

// ---------------------------------------------------------------------------
// CopyButton
// ---------------------------------------------------------------------------

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false)
  const onClick = async () => {
    const fallback = () => {
      const ta = document.createElement('textarea')
      ta.value = value
      ta.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0'
      document.body.appendChild(ta)
      ta.focus()
      ta.select()
      try {
        document.execCommand('copy')
      } catch {
        /* ignore */
      }
      document.body.removeChild(ta)
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value)
      } else {
        fallback()
      }
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      fallback()
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }
  return (
    <Button variant="ghost" size="sm" onClick={onClick} title={label ?? 'Copy'}>
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  )
}

// ---------------------------------------------------------------------------
// KeyValue row
// ---------------------------------------------------------------------------

function KV({
  label,
  value,
  mono = true,
  copy = true,
}: {
  label: string
  value: string | undefined
  mono?: boolean
  copy?: boolean
}) {
  if (!value) return null
  return (
    <div className="grid grid-cols-[140px_1fr_auto] items-center gap-3 border-b border-[var(--border)]/40 py-2 last:border-b-0">
      <span className="text-xs uppercase tracking-wide text-[var(--fg-muted)]">{label}</span>
      <span className={mono ? 'font-mono text-xs break-all text-[var(--fg)]' : 'text-sm text-[var(--fg)]'}>
        {value}
      </span>
      {copy ? <CopyButton value={value} label={`Copy ${label}`} /> : <span />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Session card
// ---------------------------------------------------------------------------

function SessionCard() {
  const { token, logout } = useAuth()
  const claims = decodeJwt(token)

  return (
    <Card className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-[var(--fg)]">Current Session</h3>
          <p className="text-xs text-[var(--fg-muted)]">
            Access token stored in <code>localStorage</code> as <code>{TOKEN_KEY}</code>.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={logout}>
          Sign out
        </Button>
      </div>

      {!token ? (
        <p className="text-sm text-[var(--fg-muted)]">No active session.</p>
      ) : (
        <>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-[var(--fg-muted)]">Access Token</span>
              <CopyButton value={token} label="Copy access token" />
            </div>
            <div className="max-h-40 overflow-y-auto rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 font-mono text-[11px] leading-relaxed text-[var(--fg)] break-all">
              {token}
            </div>
            <p className="mt-2 text-xs text-[var(--fg-muted)]">
              Use as <code>Authorization: Bearer &lt;token&gt;</code>.
            </p>
          </div>

          {claims && (
            <div>
              <div className="mb-2 text-xs uppercase tracking-wide text-[var(--fg-muted)]">
                Decoded Claims
              </div>
              <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3">
                <KV label="sub" value={String(claims.sub ?? '')} />
                <KV label="preferred_username" value={String(claims.preferred_username ?? '')} />
                <KV label="email" value={String(claims.email ?? '')} />
                <KV label="aud" value={Array.isArray(claims.aud) ? claims.aud.join(', ') : String(claims.aud ?? '')} />
                <KV label="iss" value={String(claims.iss ?? '')} />
                <KV label="iat" value={fmtUnix(claims.iat)} mono={false} copy={false} />
                <KV
                  label="exp"
                  value={`${fmtUnix(claims.exp)} ${expiresInLabel(claims.exp)}`}
                  mono={false}
                  copy={false}
                />
              </div>
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-[var(--fg-muted)] hover:text-[var(--fg)]">
                  Show full payload
                </summary>
                <pre className="mt-2 max-h-60 overflow-auto rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 text-[11px] text-[var(--fg)]">
                  {JSON.stringify(claims, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// IDP Config card
// ---------------------------------------------------------------------------

function IdpConfigCard() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['auth-info'],
    queryFn: getAuthInfo,
  })

  return (
    <Card className="p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-[var(--fg)]">IDP Configuration</h3>
          <p className="text-xs text-[var(--fg-muted)]">
            Discovered from the backend at <code>GET /auth/info</code>.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => refetch()} title="Refetch">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : isError || !data?.ok ? (
        <p className="text-sm text-[var(--destructive)]">
          {data?.error || 'Failed to load IDP configuration.'}
        </p>
      ) : (
        <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3">
          <KV label="Issuer" value={data.issuer} />
          <KV label="Client ID" value={data.client_id} />
          <KV label="Authorization" value={data.authorization_endpoint} />
          <KV label="Token" value={data.token_endpoint} />
          <KV label="Device Auth" value={data.device_authorization_endpoint} />
          <KV label="JWKS" value={data.jwks_uri} />
          <KV label="Scopes" value={data.scopes} />
        </div>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Device Flow card
// ---------------------------------------------------------------------------

function DeviceFlowCard() {
  const [flow, setFlow] = useState<DeviceFlowStartResponse | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [tokenExpiresIn, setTokenExpiresIn] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [polling, setPolling] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const intervalRef = useRef<number | null>(null)
  const expiresAtRef = useRef<number>(0)
  const pollEverRef = useRef<number>(5)

  const clearTimers = () => {
    if (intervalRef.current !== null) {
      window.clearTimeout(intervalRef.current)
      intervalRef.current = null
    }
  }

  useEffect(() => () => clearTimers(), [])

  const start = async () => {
    setError(null)
    setToken(null)
    setRefreshToken(null)
    setTokenExpiresIn(null)
    setFlow(null)
    try {
      const f = await startDeviceFlow()
      setFlow(f)
      pollEverRef.current = f.interval || 5
      expiresAtRef.current = Date.now() + (f.expires_in || 300) * 1000
      setPolling(true)
      schedulePoll(f)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start device flow')
    }
  }

  const schedulePoll = (f: DeviceFlowStartResponse) => {
    clearTimers()
    intervalRef.current = window.setTimeout(() => poll(f), pollEverRef.current * 1000)
  }

  const poll = async (f: DeviceFlowStartResponse) => {
    if (Date.now() > expiresAtRef.current) {
      setError('Code expired. Please start again.')
      setPolling(false)
      return
    }
    try {
      const r = await pollDeviceFlow(f.device_code, f.token_url, f.client_id)
      if (r.status === 'ok' && r.access_token) {
        setToken(r.access_token)
        setRefreshToken(r.refresh_token ?? null)
        setTokenExpiresIn(r.expires_in ?? null)
        setPolling(false)
        return
      }
      if (r.status === 'pending') {
        schedulePoll(f)
        return
      }
      if (r.status === 'slow_down') {
        pollEverRef.current += 5
        schedulePoll(f)
        return
      }
      setError(r.detail || r.status)
      setPolling(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Polling failed')
      setPolling(false)
    }
  }

  // Countdown
  useEffect(() => {
    if (!polling) return
    const t = window.setInterval(() => {
      const s = Math.max(0, Math.round((expiresAtRef.current - Date.now()) / 1000))
      setSecondsLeft(s)
    }, 1000)
    return () => window.clearInterval(t)
  }, [polling])

  const reset = () => {
    clearTimers()
    setFlow(null)
    setToken(null)
    setRefreshToken(null)
    setTokenExpiresIn(null)
    setError(null)
    setPolling(false)
  }

  const useToken = () => {
    if (!token) return
    localStorage.setItem(TOKEN_KEY, token)
    toast.success('Token saved — reload to use it for API calls.')
  }

  return (
    <Card className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-[var(--fg)]">Device Flow Token</h3>
          <p className="text-xs text-[var(--fg-muted)]">
            Generate a JWT via OIDC device flow — useful for CLI scripts and automation.
          </p>
        </div>
        {(flow || token || error) && (
          <Button variant="ghost" size="sm" onClick={reset}>
            Reset
          </Button>
        )}
      </div>

      {!flow && !token && (
        <Button onClick={start}>
          <KeyRound className="h-4 w-4" />
          Start device flow
        </Button>
      )}

      {flow && !token && (
        <div className="space-y-3">
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-4">
            <div className="text-xs uppercase tracking-wide text-[var(--fg-muted)]">
              Open this URL in your browser
            </div>
            <a
              href={flow.verification_uri_complete}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-2 break-all text-sm text-[var(--primary)] hover:underline"
            >
              {flow.verification_uri_complete}
              <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
          </div>

          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-4 text-center">
            <div className="text-xs uppercase tracking-wide text-[var(--fg-muted)]">
              Enter this code when prompted
            </div>
            <div className="mt-2 font-mono text-2xl tracking-widest text-[var(--primary)]">
              {flow.user_code}
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-md bg-[var(--bg-elevated)] px-3 py-2 text-sm">
            {polling && <Spinner size="sm" />}
            <span className="text-[var(--fg-muted)]">Waiting for you to authorise…</span>
            <span className="ml-auto text-xs text-[var(--fg-muted)]">{secondsLeft}s remaining</span>
          </div>
        </div>
      )}

      {token && (
        <div className="space-y-3">
          <div className="rounded-md border border-green-500/30 bg-green-500/10 px-3 py-2 text-sm text-green-400">
            Token received{tokenExpiresIn ? ` — expires in ${Math.round(tokenExpiresIn / 60)} min` : ''}
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-[var(--fg-muted)]">Access Token</span>
              <CopyButton value={token} />
            </div>
            <div className="max-h-32 overflow-y-auto rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 font-mono text-[11px] break-all text-[var(--fg)]">
              {token}
            </div>
          </div>

          {refreshToken && (
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs uppercase tracking-wide text-[var(--fg-muted)]">Refresh Token</span>
                <CopyButton value={refreshToken} />
              </div>
              <div className="max-h-32 overflow-y-auto rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] p-3 font-mono text-[11px] break-all text-[var(--fg)]">
                {refreshToken}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button variant="outline" onClick={useToken}>
              Use this token for the current session
            </Button>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-[var(--destructive)]/30 bg-[var(--destructive)]/10 px-3 py-2 text-sm text-[var(--destructive)]">
          {error}
        </div>
      )}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AuthPage() {
  return (
    <AppShell title="Auth & Tokens">
      <div className="mx-auto max-w-4xl space-y-6">
        <SessionCard />
        <IdpConfigCard />
        <DeviceFlowCard />
      </div>
    </AppShell>
  )
}
